import React from 'react';

import type { ThemeType } from '../../../@types';
import { ReactComponent as Building } from '../../../assets/icons/building.svg';
import { ReactComponent as CampusLocations } from '../../../assets/icons/campus_locations.svg';
import { ReactComponent as Car } from '../../../assets/icons/car.svg';
import { ReactComponent as CheckIcon } from '../../../assets/icons/check.svg';
import { ReactComponent as CheckCircleIcon } from '../../../assets/icons/check_circle.svg';
import { ReactComponent as CloseIcon } from '../../../assets/icons/close.svg';
import { ReactComponent as CurrentLocation } from '../../../assets/icons/current_location.svg';
import { ReactComponent as DownIcon } from '../../../assets/icons/down.svg';
import { ReactComponent as ElevatorIcon } from '../../../assets/icons/elevator.svg';
import { ReactComponent as HamburgerIcon } from '../../../assets/icons/hamburger.svg';
import { ReactComponent as HamburgerOpenIcon } from '../../../assets/icons/hamburger-open.svg';
import { ReactComponent as Info } from '../../../assets/icons/info.svg';
import { ReactComponent as LeftIcon } from '../../../assets/icons/left.svg';
import { ReactComponent as Location } from '../../../assets/icons/location.svg';
import { ReactComponent as LocationOpen } from '../../../assets/icons/location_on.svg';
import { ReactComponent as MapLocation } from '../../../assets/icons/map-location.svg';
import { ReactComponent as MoreVert } from '../../../assets/icons/more_vert.svg';
import { ReactComponent as NoResultsIcon } from '../../../assets/icons/no-results.svg';
import { ReactComponent as PinIcon } from '../../../assets/icons/pin.svg';
import { ReactComponent as PinActiveIcon } from '../../../assets/icons/pin_active.svg';
import { ReactComponent as Print } from '../../../assets/icons/print.svg';
import { ReactComponent as ProgressActivity } from '../../../assets/icons/progress_activity.svg';
import { ReactComponent as RightIcon } from '../../../assets/icons/right.svg';
import { ReactComponent as SearchIcon } from '../../../assets/icons/search.svg';
import { ReactComponent as SendEmail } from '../../../assets/icons/send_email.svg';
import { ReactComponent as SendMobile } from '../../../assets/icons/send_mobile.svg';
import { ReactComponent as StartIndicator } from '../../../assets/icons/start-indicator.svg';
import { ReactComponent as UpIcon } from '../../../assets/icons/up.svg';
import { ReactComponent as Walking } from '../../../assets/icons/walking.svg';
import { ReactComponent as YouAreHere } from '../../../assets/icons/you_are_here.svg';
import styles from './Icon.module.scss';
import { IconType } from './types';

interface IconProps {
  icon: IconType;
  size?: number | 'full';
  theme?: ThemeType | 'none';
  color?: string;
  ariaLabel?: string;
  ariaRole?: string;
}

const Icon: React.FC<IconProps> = ({
  icon,
  size = 24,
  theme,
  color = 'inherit',
  ariaLabel,
  ariaRole,
}) => {
  const iconSize = size === 'full' ? '100%' : size;

  const IconComponent = (): React.ReactElement => {
    const iconList = {
      [IconType.BUILDING]: <Building />,
      [IconType.CAR]: <Car />,
      [IconType.CHECK]: <CheckIcon />,
      [IconType.CHECK_CIRCLE]: <CheckCircleIcon />,
      [IconType.CLOSE]: <CloseIcon />,
      [IconType.DOWN]: <DownIcon />,
      [IconType.ELEVATOR]: <ElevatorIcon />,
      [IconType.HAMBURGER_OPEN]: <HamburgerOpenIcon />,
      [IconType.HAMBURGER]: <HamburgerIcon />,
      [IconType.INFO]: <Info />,
      [IconType.LEFT]: <LeftIcon />,
      [IconType.LOCATION]: <Location />,
      [IconType.LOCATION_OPEN]: <LocationOpen />,
      [IconType.MAP_LOCATION]: <MapLocation />,
      [IconType.MORE_VERT]: <MoreVert />,
      [IconType.NO_RESULTS]: <NoResultsIcon />,
      [IconType.PIN]: <PinIcon />,
      [IconType.PIN_ACTIVE]: <PinActiveIcon />,
      [IconType.RIGHT]: <RightIcon />,
      [IconType.SEARCH]: <SearchIcon />,
      [IconType.START_INDICATOR]: <StartIndicator />,
      [IconType.UP]: <UpIcon />,
      [IconType.PROGRESS_ACTIVITY]: <ProgressActivity />,
      [IconType.YOU_ARE_HERE]: <YouAreHere />,
      [IconType.CURRENT_LOCATION]: <CurrentLocation />,
      [IconType.CAMPUS_LOCATIONS]: <CampusLocations />,
      [IconType.SEND_MOBILE]: <SendMobile />,
      [IconType.PRINT]: <Print />,
      [IconType.SEND_EMAIL]: <SendEmail />,
      [IconType.WALKING]: <Walking />,
    };
    return iconList[icon];
  };

  return (
    <div
      className={`${styles.icon} ${theme ? styles[theme] : ''}`}
      style={{
        width: iconSize,
        height: iconSize,
        color,
        pointerEvents: 'none',
      }}
      data-testid="icon"
      aria-label={ariaLabel}
      role={ariaRole}
    >
      <IconComponent />
    </div>
  );
};

export default Icon;
